// src/app/settings/accruals/pages/leave-policies/leave-policies.component.ts

import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { AccrualsService } from '../accruals.service';
import {
  EmployeeUser,
  LeavePolicy,
  LeavePolicyFilterValues,
  UserFilterValues,
} from '../models/accruals.model';
import { CreateLeavePolicyModalComponent } from '../shared/create-leave-policy-modal.component';
import { AccrualsNavComponent } from '../../../shared/accruals-nav/accruals-nav.component';

interface PolicyAssignmentFiltersVm {
  searchTerm: string;
  companyId: number | null;
  departmentId: number | null;
}

@Component({
  selector: 'app-leave-policies',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, CreateLeavePolicyModalComponent, AccrualsNavComponent],
  templateUrl: './leave-policies.component.html',
  styleUrls: ['./leave-policies.component.scss'],
})
export class LeavePoliciesComponent implements OnInit, OnDestroy {
  policies$: Observable<LeavePolicy[]> = of([]);
  isLoading = false;
  isSavingPolicy = false;
  isAssigningPolicy = false;
  errorMessage: string | null = null;

  leavePolicyFilterValues: LeavePolicyFilterValues | null = null;
  userFilterValues: UserFilterValues | null = null;
  activeUsers: EmployeeUser[] = [];

  vacationPolicies: LeavePolicy[] = [];
  sickPolicies: LeavePolicy[] = [];

  createModalOpen = false;
  assignModalOpen = false;
  selectedPolicyForAssignment: LeavePolicy | null = null;

  assignmentStartDate = '';
  selectedEmployeeIds = new Set<number>();

  assignmentFilters: PolicyAssignmentFiltersVm = {
    searchTerm: '',
    companyId: null,
    departmentId: null,
  };

  assignmentSearchInputValue = '';
  private readonly destroy$ = new Subject<void>();

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private readonly accrualsService: AccrualsService,
    private readonly cd: ChangeDetectorRef,
    private readonly router: Router,
  ) { }

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.unlockPageScroll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasAssignmentFilters(): boolean {
    return !!(
      this.assignmentFilters.searchTerm.trim() ||
      this.assignmentFilters.companyId !== null ||
      this.assignmentFilters.departmentId !== null
    );
  }

  get filteredAssignableUsers(): EmployeeUser[] {
    const search = this.assignmentFilters.searchTerm.trim().toLowerCase();

    return this.activeUsers.filter((user) => {
      const matchesSearch =
        !search ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(search) ||
        user.employeeNumber.toLowerCase().includes(search) ||
        (user.email ?? '').toLowerCase().includes(search);

      const matchesCompany =
        this.assignmentFilters.companyId === null ||
        user.company?.id === this.assignmentFilters.companyId;

      const matchesDepartment =
        this.assignmentFilters.departmentId === null ||
        user.department?.id === this.assignmentFilters.departmentId;

      return matchesSearch && matchesCompany && matchesDepartment;
    });
  }

  get assignmentSelectedCount(): number {
    return this.selectedEmployeeIds.size;
  }

  loadInitialData(): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.cd.detectChanges();

    forkJoin({
      policies: this.accrualsService.getAllLeavePolicies(),
      leavePolicyFilterValues: this.accrualsService.getLeavePolicyFilterValues(),
      userFilterValues: this.accrualsService.getUserFilterValues(),
      activeUsers: this.accrualsService.getAllActiveUsers(),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ policies, leavePolicyFilterValues, userFilterValues, activeUsers }) => {
          const allPolicies = policies ?? [];

          this.leavePolicyFilterValues = leavePolicyFilterValues;
          this.userFilterValues = userFilterValues;
          this.activeUsers = (activeUsers ?? []).filter(
            (user) => user.employeeStatus === 'Active' && !user.isDeleted,
          );

          this.vacationPolicies = allPolicies.filter((p) => p.leaveType === 'Vacation');
          this.sickPolicies = allPolicies.filter((p) => p.leaveType === 'Sick');
          this.policies$ = of(allPolicies);

          this.cd.detectChanges();
        },
        error: () => {
          this.errorMessage = 'Failed to load leave policies';
          this.vacationPolicies = [];
          this.sickPolicies = [];
          this.policies$ = of([]);
          this.cd.detectChanges();
        },
      });
  }

  onAssignmentSearchChange(value: string | null): void {
    const normalized = String(value ?? '').trimStart();
    this.assignmentSearchInputValue = normalized;
    this.assignmentFilters.searchTerm = normalized;
    this.cd.detectChanges();
  }

  onAssignmentCompanyChange(value: number | string | null): void {
    this.assignmentFilters.companyId = this.toNullableNumber(value);
    this.cd.detectChanges();
  }

  onAssignmentDepartmentChange(value: number | string | null): void {
    this.assignmentFilters.departmentId = this.toNullableNumber(value);
    this.cd.detectChanges();
  }

  clearAssignmentFilters(): void {
    this.assignmentFilters = {
      searchTerm: '',
      companyId: null,
      departmentId: null,
    };
    this.assignmentSearchInputValue = '';
    this.cd.detectChanges();
  }

  openCreatePolicyModal(): void {
    this.createModalOpen = true;
    this.lockPageScroll();
    this.cd.detectChanges();
  }

  closeCreatePolicyModal(): void {
    this.createModalOpen = false;
    this.unlockPageScroll();
    this.cd.detectChanges();
  }

  onCreatePolicy(payload: {
    name: string;
    leaveType: 'Vacation' | 'Sick';
    accrualFrequency: 'Daily' | 'Weekly' | 'PerPayPeriod' | 'Monthly' | 'YearlyFrontLoad';
    weeklyAccrualDay?: number | null;
    hoursPerYear: number;
    maxBalance: number;
    maxCarryOver: number;
    allowNegativeBalance: boolean;
  }): void {
    this.isSavingPolicy = true;
    this.cd.detectChanges();

    const requestBody: {
      name: string;
      leaveType: 'Vacation' | 'Sick';
      accrualFrequency: 'Daily' | 'Weekly' | 'PerPayPeriod' | 'Monthly' | 'YearlyFrontLoad';
      weeklyAccrualDay?: number | null;
      hoursPerYear: number;
      maxBalance: number;
      maxCarryOver: number;
      allowNegativeBalance: boolean;
    } = {
      name: payload.name,
      leaveType: payload.leaveType,
      accrualFrequency: payload.accrualFrequency,
      hoursPerYear: payload.hoursPerYear,
      maxBalance: payload.maxBalance,
      maxCarryOver: payload.maxCarryOver,
      allowNegativeBalance: payload.allowNegativeBalance,
    };

    if (payload.accrualFrequency === 'Weekly') {
      requestBody.weeklyAccrualDay = payload.weeklyAccrualDay;
    }

    this.accrualsService
      .createLeavePolicy(requestBody)
      .pipe(
        finalize(() => {
          this.isSavingPolicy = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((success) => {
        if (!success) {
          this.showToast('Failed to create leave policy', 'error');
          return;
        }

        this.createModalOpen = false;
        this.unlockPageScroll();
        this.showToast('Leave policy created successfully', 'success');
        this.loadInitialData();
      });
  }

  openAssignPolicyModal(policy: LeavePolicy): void {
    this.selectedPolicyForAssignment = policy;
    this.assignmentStartDate = '';
    this.selectedEmployeeIds.clear();

    this.assignmentFilters = {
      searchTerm: '',
      companyId: null,
      departmentId: null,
    };
    this.assignmentSearchInputValue = '';

    this.assignModalOpen = true;
    this.lockPageScroll();
    this.cd.detectChanges();
  }

  closeAssignPolicyModal(): void {
    this.assignModalOpen = false;
    this.selectedPolicyForAssignment = null;
    this.assignmentStartDate = '';
    this.selectedEmployeeIds.clear();
    this.clearAssignmentFilters();
    this.unlockPageScroll();
    this.cd.detectChanges();
  }

  toggleEmployeeSelection(employeeId: number): void {
    if (this.selectedEmployeeIds.has(employeeId)) {
      this.selectedEmployeeIds.delete(employeeId);
    } else {
      this.selectedEmployeeIds.add(employeeId);
    }

    this.cd.detectChanges();
  }

  isEmployeeSelected(employeeId: number): boolean {
    return this.selectedEmployeeIds.has(employeeId);
  }

  assignSelectedEmployees(): void {
    if (!this.selectedPolicyForAssignment) {
      return;
    }

    if (!this.assignmentStartDate || this.selectedEmployeeIds.size === 0) {
      this.showToast('Select employees and a start date before saving', 'error');
      return;
    }

    this.isAssigningPolicy = true;
    this.cd.detectChanges();

    this.accrualsService
      .assignEmployeesToPolicy(this.selectedPolicyForAssignment.id, {
        employeeIds: [...this.selectedEmployeeIds],
        startDate: this.assignmentStartDate,
      })
      .pipe(
        finalize(() => {
          this.isAssigningPolicy = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((success) => {
        if (!success) {
          this.showToast('Failed to assign policy to employees', 'error');
          return;
        }

        this.closeAssignPolicyModal();
        this.showToast('Policy assigned successfully', 'success');
        this.loadInitialData();
      });
  }

  goToPolicyDetail(policy: LeavePolicy): void {
    this.router.navigate(['/settings/accruals/policies', policy.id]);
  }

  trackByPolicyId(_: number, item: LeavePolicy): number {
    return item.id;
  }

  trackByEmployeeId(_: number, item: EmployeeUser): number {
    return item.id;
  }

  private lockPageScroll(): void {
    document.body.classList.add('modal-open');
  }

  private unlockPageScroll(): void {
    if (!this.createModalOpen && !this.assignModalOpen) {
      document.body.classList.remove('modal-open');
    }
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    this.cd.detectChanges();

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.markForCheck();
    }, 2500);
  }
}